import { defineStore } from "pinia"
import { toRaw } from "vue"
import { type LayerTypeValue, type Opportunity, type SearchFilter, EvaluationStatus } from "@/model/DataModel"
import {
	UseRootStore,
	Permission,
	applySearchFilterToFilters,
	defaultFilters,
	searchFilterFromFilters,
	type Filters,
} from "@/model/RootStore"
import { RANGE_MAX } from "@/types"
import * as apiClient from "@/utils/ApiClient"
import { mergeWithDefaults } from "@/utils"
import { getMapModeStorageItem, setMapModeStorageItem } from "@/stores/projectStore"

type CustomViewSnapshotOptions = {
	filterCount: number
	layerCount: number
	mapStyle?: string
}

type CreateCustomViewSnapshotOptions = CustomViewSnapshotOptions & {
	name: string
	projectId?: string | null
}

function jsonClone<T>(value: T): T {
	return JSON.parse(JSON.stringify(value)) as T
}

// the active saved search is remembered across sessions in map mode only, never in hunting mode
const ACTIVE_CUSTOM_VIEW_STORAGE_KEY = "active-custom-view-id"

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

function stringArray(value: unknown): string[] | null {
	if (!Array.isArray(value)) return null
	const values = value.filter((item): item is string => typeof item === "string")
	return values.length === value.length ? values : null
}

function serializeFiltersForCustomView(filters: Filters): Record<string, unknown> {
	const rawFilters = toRaw(filters)
	return jsonClone({
		...rawFilters,
		projectStatusFilter: Array.from(rawFilters.projectStatusFilter),
	})
}

function hydrateFiltersFromCustomView(value: unknown): Filters | null {
	if (!isRecord(value)) return null

	const filters = mergeWithDefaults(defaultFilters, value) as Filters
	const projectStatusFilter = filters.projectStatusFilter
	filters.projectStatusFilter = new Set<EvaluationStatus>(
		projectStatusFilter instanceof Set
			? Array.from(projectStatusFilter)
			: Array.isArray(projectStatusFilter)
				? projectStatusFilter
				: []
	)

	// mergeWithDefaults only reconstructs Date objects when the default itself is a
	// Date; since the date range default is now null, coerce persisted strings back here.
	if (typeof filters.rangeFilters.date.min === "string") {
		const parsed = new Date(filters.rangeFilters.date.min)
		filters.rangeFilters.date.min = isNaN(parsed.getTime()) ? null : parsed
	}
	if (typeof filters.rangeFilters.date.max === "string") {
		const parsed = new Date(filters.rangeFilters.date.max)
		filters.rangeFilters.date.max = isNaN(parsed.getTime()) ? null : parsed
	}

	if (filters.rangeFilters.date.dateMaxIsToday) {
		filters.rangeFilters.date.max = new Date()
	}

	return filters
}

// hydrates a view's filterConfig, restricting its opportunity layers to the ones still available
function hydrateViewFilters(
	rootStore: ReturnType<typeof UseRootStore>,
	filterConfig: Record<string, unknown>
): Filters | null {
	const filters = hydrateFiltersFromCustomView(filterConfig.filters)
	if (!filters) return null

	const opportunityLayers = stringArray(filterConfig.opportunityLayers)
	if (opportunityLayers) {
		filters.layers = opportunityLayers.filter(layer => rootStore.availableLayers.includes(layer as LayerTypeValue))
	}

	return filters
}

function optionalArrayIntersection<T>(base: T[], overlay: T[]): T[] {
	if (base.length === 0) return [...overlay]
	if (overlay.length === 0) return [...base]
	const overlayValues = new Set(overlay)
	return base.filter(value => overlayValues.has(value))
}

function requiredArrayIntersection<T>(base: T[], overlay: T[]): T[] {
	if (base.length === 0 || overlay.length === 0) return []
	const overlayValues = new Set(overlay)
	return base.filter(value => overlayValues.has(value))
}

function combineNumberRange(
	base: { min: number | null; max: number | null },
	overlay: { min: number | null; max: number | null },
	defaultMax: number | null
): { min: number; max: number | null } {
	const baseMin = base.min ?? 0
	const overlayMin = overlay.min ?? 0
	const baseMax = base.max ?? defaultMax
	const overlayMax = overlay.max ?? defaultMax
	const max =
		baseMax == null && overlayMax == null
			? null
			: Math.min(baseMax ?? Number.POSITIVE_INFINITY, overlayMax ?? Number.POSITIVE_INFINITY)

	return {
		min: Math.max(baseMin, overlayMin),
		max,
	}
}

function toDate(value: Date | string | null): Date {
	return value instanceof Date ? value : new Date(value ?? 0)
}

function formatSearchFilterDate(date: Date): Date {
	return new Date(date.toISOString().split("T")[0])
}

function combineDateRange(
	base: { min: Date | string | null; max: Date | string | null },
	overlay: { min: Date | string | null; max: Date | string | null }
): { min: Date; max: Date } {
	const min = new Date(Math.max(toDate(base.min).getTime(), toDate(overlay.min).getTime()))
	const max = new Date(Math.min(toDate(base.max).getTime(), toDate(overlay.max).getTime()))

	return {
		min: formatSearchFilterDate(min),
		max: formatSearchFilterDate(max),
	}
}

function combineSearchFilters(base: SearchFilter, overlay: SearchFilter): SearchFilter {
	return {
		...base,
		opportunityTypes: requiredArrayIntersection(base.opportunityTypes, overlay.opportunityTypes),
		developmentTypes: optionalArrayIntersection(base.developmentTypes, overlay.developmentTypes),
		regions: optionalArrayIntersection(base.regions, overlay.regions),
		municipalities: optionalArrayIntersection(base.municipalities, overlay.municipalities),
		boroughs: optionalArrayIntersection(base.boroughs, overlay.boroughs),
		price: combineNumberRange(base.price, overlay.price, RANGE_MAX),
		landArea: combineNumberRange(base.landArea, overlay.landArea, RANGE_MAX),
		buildableLandArea: combineNumberRange(base.buildableLandArea, overlay.buildableLandArea, RANGE_MAX),
		residentialUnits: combineNumberRange(base.residentialUnits, overlay.residentialUnits, null),
		launchDate: combineDateRange(base.launchDate, overlay.launchDate),
		assetTypes: optionalArrayIntersection(base.assetTypes, overlay.assetTypes),
		landCategories: optionalArrayIntersection(base.landCategories, overlay.landCategories),
		landSubcategories: optionalArrayIntersection(base.landSubcategories, overlay.landSubcategories),
	} as SearchFilter
}

function opportunitySnapshotIds(opportunities: Opportunity[]): number[] {
	return opportunities.map(opportunity => Number(opportunity.id)).filter(Number.isFinite)
}

// TODO use new style for pinia store definition
export const useCustomViewStore = defineStore("custom-view", {
	state: () => ({
		customViews: [] as apiClient.CustomView[],
		customViewsLoaded: false,
		customViewsProjectId: null as string | null,
		customViewsLoading: false,
		customViewsError: null as string | null,
		activeCustomViewId: null as string | null,
		// true until the first fetch settles, so the UI can hold off instead of flashing "no active search"
		isRestoringActiveCustomView: getMapModeStorageItem(ACTIVE_CUSTOM_VIEW_STORAGE_KEY) !== null,
	}),
	actions: {
		// single mutation point for activeCustomViewId so persistence stays consistent everywhere
		setActiveCustomView(id: string | null): void {
			this.activeCustomViewId = id
			setMapModeStorageItem(ACTIVE_CUSTOM_VIEW_STORAGE_KEY, id)
		},

		clearActiveCustomView(): void {
			this.setActiveCustomView(null)
		},

		async fetchCustomViews(projectId: string | null = null, force = false): Promise<apiClient.CustomView[]> {
			const rootStore = UseRootStore()
			if (!rootStore.isUserLoggedIn) return []

			const scopedProjectId = projectId ?? null
			if (this.customViewsLoaded && this.customViewsProjectId === scopedProjectId && !force) return this.customViews
			if (this.customViewsProjectId !== scopedProjectId) {
				this.customViews = []
				this.customViewsLoaded = false
				this.setActiveCustomView(null)
			}

			this.customViewsLoading = true
			this.customViewsError = null
			try {
				this.customViews = await apiClient.fetchCustomViews(rootStore.userApiToken, scopedProjectId)
				this.customViewsLoaded = true
				this.customViewsProjectId = scopedProjectId
				if (this.activeCustomViewId && !this.customViews.some(view => view.id === this.activeCustomViewId)) {
					this.setActiveCustomView(null)
				}

				if (!this.activeCustomViewId) {
					const persistedId = getMapModeStorageItem(ACTIVE_CUSTOM_VIEW_STORAGE_KEY)
					if (persistedId && this.customViews.some(view => view.id === persistedId)) {
						this.activeCustomViewId = persistedId
					}
				}

				return this.customViews
			} catch (error) {
				this.customViewsError = error instanceof Error ? error.message : String(error)
				throw error
			} finally {
				this.customViewsLoading = false
				this.isRestoringActiveCustomView = false
			}
		},

		async fetchCustomView(id: string): Promise<apiClient.CustomView | null> {
			const existingView = this.customViews.find(view => view.id === id)

			if (existingView) {
				return existingView
			}

			const rootStore = UseRootStore()

			return await apiClient.fetchCustomView(rootStore.userApiToken, id)
		},

		customViewSnapshotFromCurrentSearch(options: CustomViewSnapshotOptions) {
			const rootStore = UseRootStore()
			const opportunityIds = opportunitySnapshotIds(rootStore.opportunities)
			return {
				filterConfig: {
					searchFilter: jsonClone(toRaw(rootStore.searchFilter)),
					filters: serializeFiltersForCustomView(rootStore.filters),
					visibleLayers: [...rootStore.visibleLayers],
					opportunityLayers: [...rootStore.filters.layers],
					metadata: {
						filterCount: options.filterCount,
						layerCount: options.layerCount,
					},
				},
				sortConfig: {},
				displayConfig: {
					filterCount: options.filterCount,
					layerCount: options.layerCount,
					resultCount: opportunityIds.length,
					mapStyle: options.mapStyle ?? null,
				},
				opportunityIds,
			}
		},

		// same shape as customViewSnapshotFromCurrentSearch, but hydrated from the view's saved content
		comparableFilterConfigForView(view: apiClient.CustomView): Record<string, unknown> | null {
			if (!isRecord(view.filterConfig)) return null

			const filters = hydrateViewFilters(UseRootStore(), view.filterConfig)
			if (!filters) return null

			return {
				searchFilter: jsonClone(searchFilterFromFilters(filters)),
				filters: serializeFiltersForCustomView(filters),
				visibleLayers: stringArray(view.filterConfig.visibleLayers) ?? [],
				opportunityLayers: [...filters.layers],
				metadata: {},
			}
		},

		async createCustomViewFromCurrentSearch(options: CreateCustomViewSnapshotOptions): Promise<apiClient.CustomView> {
			const rootStore = UseRootStore()
			if (!rootStore.isUserLoggedIn) {
				throw new Error("Authentication required")
			}

			const projectId = options.projectId ?? null
			const view = await apiClient.createCustomView(rootStore.userApiToken, {
				name: options.name,
				projectId,
				...this.customViewSnapshotFromCurrentSearch(options),
			})

			this.customViews = [view, ...this.customViews.filter(existing => existing.id !== view.id)]
			this.customViewsLoaded = true
			this.customViewsProjectId = projectId
			this.setActiveCustomView(view.id)

			return view
		},

		async updateCustomViewFromCurrentSearch(
			id: string,
			options: CustomViewSnapshotOptions
		): Promise<apiClient.CustomView> {
			const rootStore = UseRootStore()
			if (!rootStore.isUserLoggedIn) {
				throw new Error("Authentication required")
			}

			const updated = await apiClient.updateCustomView(
				rootStore.userApiToken,
				id,
				this.customViewSnapshotFromCurrentSearch(options)
			)
			this.customViews = this.customViews.map(view => (view.id === id ? updated : view))
			this.setActiveCustomView(updated.id)
			return updated
		},

		async updateCustomViewName(id: string, name: string): Promise<apiClient.CustomView> {
			const rootStore = UseRootStore()
			const updated = await apiClient.updateCustomView(rootStore.userApiToken, id, { name })
			this.customViews = this.customViews.map(view => (view.id === id ? updated : view))
			return updated
		},

		async deleteCustomView(id: string): Promise<void> {
			const rootStore = UseRootStore()
			await apiClient.deleteCustomView(rootStore.userApiToken, id)
			this.customViews = this.customViews.filter(view => view.id !== id)
			if (this.activeCustomViewId === id) {
				this.setActiveCustomView(null)
			}
		},

		applyCustomView(view: apiClient.CustomView, baseSearchFilter: SearchFilter | null = null): apiClient.CustomView {
			const rootStore = UseRootStore()

			if (isRecord(view.filterConfig)) {
				const filters = hydrateViewFilters(rootStore, view.filterConfig)
				if (filters) {
					if (baseSearchFilter) {
						applySearchFilterToFilters(
							filters,
							combineSearchFilters(baseSearchFilter, searchFilterFromFilters(filters)),
							rootStore.currentUser.has(Permission.TransactionRead)
						)
					}
					rootStore.filters = filters
				} else if (isRecord(view.filterConfig.searchFilter)) {
					const searchFilter = view.filterConfig.searchFilter as unknown as SearchFilter
					rootStore.setFilters(baseSearchFilter ? combineSearchFilters(baseSearchFilter, searchFilter) : searchFilter)
				}

				const visibleLayers = stringArray(view.filterConfig.visibleLayers)
				if (visibleLayers) {
					rootStore.setVisibleLayers(visibleLayers)
				}
			}

			this.setActiveCustomView(view.id)
			return view
		},

		selectCustomView(id: string, baseSearchFilter: SearchFilter | null = null): apiClient.CustomView | null {
			const view = this.customViews.find(customView => customView.id === id)
			return view ? this.applyCustomView(view, baseSearchFilter) : null
		},
	},
})
